import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen } from "@kundeninfo/ui";
import { Optionen } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { TicketBoard } from "./TicketBoard.js";
import { TicketListe } from "./TicketListe.js";
import { TicketDialog } from "./TicketDialog.js";
import type { TicketDialogProps } from "./TicketDialog.js";
import { TicketAnlegen } from "./TicketAnlegen.js";
import { TicketVerknuepfungen } from "./TicketVerknuepfungen.js";
import { leseFilter } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { TicketsClient } from "./ticketsClient.js";
import type { VerlassenWaechter } from "../../router.js";
import "./Tickets.css";

/**
 * DER BEREICH „TICKETS" (Spec „Board › Aufbau", F43): Board | Liste (Adresse, F19), der
 * Arbeitsdialog über `/tickets/<id>` (Direktaufruf, schließt zurück auf die Ansicht darunter)
 * und „+ Aufgabe". Nach jeder Änderung im Dialog oder nach dem Anlegen lädt die Ansicht
 * darunter neu (`neuLaden`).
 *
 * Die vier Filter teilen Board und Liste (Ruling A4) über EIN Gedächtnis (`boardSpeicher.ts`):
 * Das Board führt und merkt sie selbst; die Liste bekommt sie von hier (`filter`/`onFilter`).
 * Der Bereich hält den Filter der Liste im Speicher der Sitzung und übernimmt beim Wechsel vom
 * Board nur, was das Board im Gedächtnis GEÄNDERT hat — so überlebt die Wahl der Liste auch
 * ohne Browserspeicher (privates Fenster), und eine Wahl im Board kommt in der Liste an.
 *
 * Der Dialog trägt die Ticket-Kennung als `key`: Wechselt die Adresse auf ein anderes Ticket,
 * beginnen Entwürfe und Startversion frisch (Review 15). Weil er an der Adresse hängt, fragt
 * ein Verlassen-Wächter (Hausmuster, `registriereVerlassenWaechter`) gestapelt nach, solange
 * im Dialog oder in „Aufgabe anlegen“ Ungespeichertes steht — Zurück-Taste, Bereichswechsel
 * und Ansichtswechsel verwerfen sonst still (Review 17). Schließt der Dialog selbst (er hat
 * dann schon gefragt), gibt der Bereich den Weg frei, damit nicht zweimal gefragt wird.
 */
export type TicketsRoute = { ansicht: "board" | "liste"; id: string | null };

export type BereichTicketsProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string; istAdmin: boolean };
  arbeitsunternehmenId: string | null; route: TicketsRoute; onNavigiere: (r: TicketsRoute) => void;
  onZurAkte: (customerId: string) => void; zusatzReiter?: TicketDialogProps["zusatzReiter"];
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

function gleicherFilter(a: BoardFilter, b: BoardFilter): boolean {
  return a.company === b.company && a.customer === b.customer && a.assignee === b.assignee && a.linked === b.linked;
}

export function BereichTickets(p: BereichTicketsProps): ReactElement {
  const [neuLaden, setNeuLaden] = useState(0);
  const [anlegen, setAnlegen] = useState<{ companyId: string | null } | null>(null);
  const companyIds = p.companies.map((c) => c.id);
  const [filter, setFilter] = useState<BoardFilter>(() => leseFilter(companyIds));
  // Das Gedächtnis beim Betreten des Boards: Beim Zurück in die Liste zählt nur, was das Board
  // daran geändert hat. Abgeglichen wird noch im selben Zeichnen (Muster „Zustand bei
  // geänderter Eingabe zurücksetzen"), damit die Liste gleich mit dem richtigen Filter lädt.
  const [beimBoard, setBeimBoard] = useState<BoardFilter | null>(() =>
    (p.route.ansicht === "board" ? leseFilter(companyIds) : null));
  const [ansichtVorher, setAnsichtVorher] = useState(p.route.ansicht);
  if (ansichtVorher !== p.route.ansicht) {
    setAnsichtVorher(p.route.ansicht);
    const jetzt = leseFilter(companyIds);
    if (p.route.ansicht === "board") setBeimBoard(jetzt);
    else if (beimBoard === null || !gleicherFilter(jetzt, beimBoard)) setFilter(jetzt);
  }

  // Ungespeichertes im Dialog (je Kennung) bzw. in „Aufgabe anlegen“ — nur solange das
  // Gemeldete zum Offenen passt (ein ausgehängter Dialog meldet nichts mehr).
  const [dialogOffen, setDialogOffen] = useState<{ id: string; ja: boolean }>({ id: "", ja: false });
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const ungespeichert = (p.route.id !== null && dialogOffen.id === p.route.id && dialogOffen.ja)
    || (anlegen !== null && anlegenOffen);
  const freigabe = useRef(false);
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!ungespeichert || registriere === undefined) return undefined;
    freigabe.current = false;
    return registriere(() => (freigabe.current ? true : darfAbbrechen(true)));
  }, [ungespeichert, registriere]);
  const dialogMeldet = useCallback((ja: boolean) => {
    if (p.route.id !== null) setDialogOffen({ id: p.route.id, ja });
  }, [p.route.id]);
  /** Ein Weg, den der Dialog selbst beschlossen hat (Speichern, Abbrechen nach seiner Frage,
   *  Anlegen): ohne zweite Frage. */
  const freigeben = () => {
    freigabe.current = true;
    setDialogOffen({ id: "", ja: false });
    setAnlegenOffen(false);
  };

  const oeffnen = (id: string) => p.onNavigiere({ ...p.route, id });
  return (
    <div className="bt">
      <div className="bt__kopf">
        <Optionen beschriftung="Ansicht" darstellung="segmente" richtung="waagerecht" wert={p.route.ansicht}
          onGeaendert={(d) => p.onNavigiere({ ansicht: d.wert === "liste" ? "liste" : "board", id: null })}>
          <option value="board">Board</option>
          <option value="liste">Liste</option>
        </Optionen>
        {p.route.ansicht === "liste" && (
          <KiKnopf art="primaer" onClick={() => setAnlegen({ companyId: null })}>+ Aufgabe</KiKnopf>
        )}
      </div>
      {p.route.ansicht === "board"
        ? <TicketBoard client={p.client} companies={p.companies} ich={p.ich} onOeffnen={oeffnen}
            onAnlegen={(companyId) => setAnlegen({ companyId })} neuLaden={neuLaden} />
        : <TicketListe client={p.client} companies={p.companies} onOeffnen={oeffnen} ichId={p.ich.id}
            neuLaden={neuLaden} filter={filter} onFilter={setFilter} />}
      {anlegen !== null && (
        <TicketAnlegen client={p.client} companies={p.companies} ich={p.ich} vorgabeCompanyId={anlegen.companyId}
          arbeitsunternehmenId={p.arbeitsunternehmenId} onUngespeichert={setAnlegenOffen}
          onAbbrechen={() => { freigeben(); setAnlegen(null); }}
          onAngelegt={(id) => { freigeben(); setAnlegen(null); setNeuLaden((n) => n + 1); oeffnen(id); }} />
      )}
      {p.route.id !== null && (
        <TicketDialog key={p.route.id} client={p.client} ticketId={p.route.id} companies={p.companies}
          ich={{ id: p.ich.id, istAdmin: p.ich.istAdmin }} onZurAkte={p.onZurAkte}
          pfad={p.route.ansicht === "liste" ? "Tickets › Liste" : "Tickets › Board"}
          onUngespeichert={dialogMeldet}
          zusatzReiter={[...(p.zusatzReiter ?? []), { id: "verknuepfungen", label: "Verknüpfungen",
            inhalt: (d, neu, nachladen) => <TicketVerknuepfungen d={d} client={p.client} onNeu={neu} nachladen={nachladen} /> }]}
          onSchliessen={(geaendert) => {
            freigeben();
            if (geaendert) setNeuLaden((n) => n + 1);
            p.onNavigiere({ ...p.route, id: null });
          }} />
      )}
    </div>
  );
}
